import { AssignmentStatus } from '../../core/data-models/assignment-status.model';
import { AssignmentType } from '../../core/data-models/assignment-type.model';
import { DroneStatus } from '../../core/data-models/drone-status.model';
import { LeaseStatus } from '../../core/data-models/lease-status.model';

/**
 * Get status badge class for Bootstrap styling
 */
export function getDroneStatusBadgeClass(status: DroneStatus): string {
  switch (status) {
    case 'available':
      return 'bg-success';
    case 'assigned':
    case 'leased':
      return 'bg-primary';
    case 'maintenance':
      return 'bg-warning';
    case 'retired':
      return 'bg-secondary';
    default:
      return 'bg-secondary';
  }
}

/**
 * Get status display label. Given the active assignment's type, an assigned drone says why it isn't
 * leased: "Assigned (Demo)", or "Assigned (Pending Lease)" until its lease is set. Assignments made
 * before ADN-2208 have no type and stay "Assigned".
 */
export function getDroneStatusLabel(status: DroneStatus, assignmentType?: AssignmentType | null): string {
  switch (status) {
    case 'available':
      return 'Available';
    case 'assigned':
      if (assignmentType === 'demo') return 'Assigned (Demo)';
      if (assignmentType === 'leased') return 'Assigned (Pending Lease)';
      return 'Assigned';
    case 'leased':
      return 'Leased';
    case 'maintenance':
      return 'Maintenance';
    case 'retired':
      return 'Retired';
    default:
      return status;
  }
}

/**
 * Get assignment status badge class
 */
export function getAssignmentStatusBadgeClass(status: AssignmentStatus): string {
  switch (status) {
    case 'active':
      return 'bg-success';
    case 'returned':
      return 'bg-secondary';
    case 'revoked':
      return 'bg-danger';
    case 'lost':
      return 'bg-danger';
    case 'damaged':
      return 'bg-warning';
    default:
      return 'bg-secondary';
  }
}

/**
 * Get assignment status label
 */
export function getAssignmentStatusLabel(status: AssignmentStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'returned':
      return 'Returned';
    case 'revoked':
      return 'Revoked';
    case 'lost':
      return 'Lost';
    case 'damaged':
      return 'Damaged';
    default:
      return status;
  }
}

/**
 * Get assignment type label ('—' for assignments made before ADN-2208, which have none)
 */
export function getAssignmentTypeLabel(type: AssignmentType | null | undefined): string {
  switch (type) {
    case 'leased':
      return 'Leased';
    case 'demo':
      return 'Demo';
    default:
      return '—';
  }
}

/**
 * Get lease status label
 */
export function getLeaseStatusLabel(status: LeaseStatus | null | undefined): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'expired':
      return 'Expired';
    case 'pending':
      return 'Pending';
    case 'terminated':
      return 'Terminated';
    default:
      return '—';
  }
}

/**
 * Whether a drone in `current` status can be switched to `target` with the status controls.
 * A leased drone can only go into maintenance: the rest of its status comes from its lease
 * (Edit Lease) or from ending the assignment (Return / Revoke). 'leased' is never a target.
 */
export function canChangeDroneStatus(current: DroneStatus | null | undefined, target: DroneStatus): boolean {
  if (target === 'leased' || current === target) return false;
  return current !== 'leased' || target === 'maintenance';
}
